import { selectAllMapComments } from "features/entity-caching/map-comments/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectSelectedLayers } from "panels/content/side-panel/slice.new"
import { UserCommentBubble } from "plugins/Toolbox/MapComments/CommentBubble"
import { useMemo } from "react"
import { MapNode } from "./MapNodes"
import { MapNodeIconProps, renderedIcon } from "plugins/plugin-registry"
import { memoize } from "nodash"
import { selectMapComment } from "features/map/inspector-panel/map-comments/module"
import { useProjectMapComments, usePrefetchMapCommentThreads } from "features/comments/Comments"
import { selectHideAllComments } from "plugins/Toolbox/MapComments/CommentSlice"
import { updateMapComment } from "features/pubsub/entityActions/cubes/comments"

const useRootByEntityId = () => {
    const { comments } = useProjectMapComments()
    return useMemo(
        () =>
            new Map(
                (comments ?? [])
                    .filter((comment) => comment.context.target === "map-pin")
                    .map((comment) => [comment.entityId, comment])
            ),
        [comments]
    )
}

const createUserCommentBubble = memoize(
    (entityId: string, userId: string) =>
        function CommentMapNode({ state }: MapNodeIconProps) {
            const root = useRootByEntityId().get(entityId)
            return (
                <UserCommentBubble
                    userId={userId}
                    state={state}
                    participantIds={root?.participantIds ?? []}
                    isUnread={root?.isUnread ?? false}
                />
            )
        },
    (entityId, userId) => `${entityId}|${userId}`
)

export const useCommentMapNodes = () => {
    const dispatch = useAppDispatch()
    const entities = useAppSelector(selectAllMapComments)
    const selectedLayers = useAppSelector(selectSelectedLayers)
    const hideAllComments = useAppSelector(selectHideAllComments)

    usePrefetchMapCommentThreads()

    const entityData = useMemo(() => {
        return entities.map<MapNode>((entity) => {
            const isActive = !!selectedLayers.find((e) => e.id === entity.id)
            return {
                id: entity.id,
                position: {
                    lat: entity.latitude,
                    lng: entity.longitude,
                },
                visible: !hideAllComments,
                locked: false,
                active: isActive,
                onClick: () => selectMapComment(entity.id),
                iconSrc: renderedIcon(createUserCommentBubble(entity.id, entity.createdBy!.userId)),
                anchor: "bottom-left",
                draggable: isActive,
                interactiveWhileCommenting: true,
                onDragEnd: ({ lat, lng }) =>
                    dispatch(
                        updateMapComment({
                            id: entity.id,
                            updates: { longitude: lng, latitude: lat },
                        })
                    ),
            }
        })
    }, [entities, selectedLayers, hideAllComments, dispatch])

    return entityData
}
